import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Instagram, Loader2, Sparkles, Ticket, Megaphone } from "lucide-react";
import { Logo } from "@/components/lunelle/Logo";
import { Countdown } from "@/components/lunelle/Countdown";
import { usePageMeta } from "@/lib/usePageMeta";
import { applyCreator, formatApiError } from "@/lib/admin-api";
import { INSTAGRAM } from "@/lib/press-data";

// Edit to match the perk set in backend/creators.py (CREATOR_PERK)
const PERK = "Early access to Lunelle before launch day.";
const PLATFORMS = [["instagram", "Instagram"], ["tiktok", "TikTok"], ["youtube", "YouTube"], ["pinterest", "Pinterest"], ["blog", "Blog"], ["other", "Other"]];
const FOLLOWERS = [["under-5k", "Under 5k"], ["5k-20k", "5k–20k"], ["20k-100k", "20k–100k"], ["100k-plus", "100k+"]];
const STEPS = [
  [Sparkles, "Apply", "Tell us about you and your audience. It takes two minutes."],
  [Check, "Get approved", "We review every application by hand and email you when you're in."],
  [Ticket, "Get your code", "Your personal invite code and link, ready for your bio and stories."],
  [Megaphone, "Share on launch day", "Post on Nov 4 with the share kit. Everyone who joins with your code counts as yours."],
];

const input = "mt-1.5 h-12 w-full rounded-xl border border-[#C86D51]/25 bg-[#FDFBF7] px-4 text-[#2C221E] outline-none transition-colors focus:border-[#9E4429]";
const Text = ({ id, label, ...p }) => (
  <label className="block text-sm text-[#5C4A42]">{label}<input id={id} data-testid={`creator-${id}-input`} className={input} {...p} /></label>
);
const Pills = ({ label, options, value, onChange, id }) => (
  <fieldset>
    <legend className="text-sm text-[#5C4A42]">{label}</legend>
    <div className="mt-2 flex flex-wrap gap-2">
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`creator-${id}-${v}`} aria-pressed={value === v}
          className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${value === v ? "border-[#9E4429] bg-[#9E4429] text-[#FBF5EE]" : "border-[#C86D51]/25 text-[#5C4A42] hover:border-[#9E4429]"}`}>{l}</button>
      ))}
    </div>
  </fieldset>
);

const ApplyForm = () => {
  const [f, setF] = useState({ name: "", email: "", handle: "", platform: "instagram", link: "", followers: "5k-20k", niche: "", message: "", consent: false, website: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const mounted = useRef(Date.now());
  const on = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.consent) { setError("Please tick the box so we can email you about the creator circle."); return; }
    setState("sending");
    try {
      const res = await applyCreator({ ...f, link: f.link || null, niche: f.niche || null, message: f.message || null, elapsed_ms: Date.now() - mounted.current });
      setState(res?.already ? "already" : "done");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
      setState("idle");
    }
  };

  if (state === "done" || state === "already") {
    return (
      <div className="rounded-[2rem] bg-[#2C221E] p-8 text-[#FBF5EE] sm:p-10" data-testid="creator-apply-success">
        <p className="font-serif text-4xl font-light">{state === "done" ? "Application received." : "You've already applied."}</p>
        <p className="mt-3 text-[#F4DED3]">{state === "done" ? "Check your inbox for a confirmation. We'll email your personal invite code as soon as you're approved." : "We have your application and will email you once it's reviewed."}</p>
        <a href={INSTAGRAM.url} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#F4DED3]/30 px-5 py-2.5 text-sm font-semibold" data-testid="creator-success-instagram"><Instagram className="h-4 w-4" />Follow {INSTAGRAM.handle} meanwhile</a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5 rounded-[2rem] border border-[#C86D51]/15 bg-white/70 p-6 sm:p-10" data-testid="creator-apply-form">
      <input type="text" name="website" value={f.website} onChange={on("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" data-testid="creator-hp" />
      <div className="grid gap-5 sm:grid-cols-2">
        <Text id="name" label="Your name" required maxLength={80} value={f.name} onChange={on("name")} autoComplete="name" />
        <Text id="email" label="Email" type="email" required value={f.email} onChange={on("email")} autoComplete="email" />
      </div>
      <Pills id="platform" label="Main platform" options={PLATFORMS} value={f.platform} onChange={set("platform")} />
      <div className="grid gap-5 sm:grid-cols-2">
        <Text id="handle" label="Your handle" required minLength={2} maxLength={60} placeholder="@yourname" value={f.handle} onChange={on("handle")} />
        <Text id="link" label="Profile link (optional)" type="url" maxLength={200} placeholder="https://" value={f.link} onChange={on("link")} />
      </div>
      <Pills id="followers" label="Audience size" options={FOLLOWERS} value={f.followers} onChange={set("followers")} />
      <Text id="niche" label="What do you create about? (optional)" maxLength={120} placeholder="Faith, wellness, cycle syncing, modest fashion..." value={f.niche} onChange={on("niche")} />
      <label className="block text-sm text-[#5C4A42]">Anything else? (optional)
        <textarea rows={3} maxLength={600} value={f.message} onChange={on("message")} data-testid="creator-message-input" className={`${input} h-auto py-3`} />
      </label>
      <label className="flex items-start gap-3 text-sm text-[#5C4A42]">
        <input type="checkbox" checked={f.consent} onChange={(e) => setF((x) => ({ ...x, consent: e.target.checked }))} data-testid="creator-consent-checkbox" className="mt-0.5 h-5 w-5 accent-[#9E4429]" />
        <span>Lunelle can email me about the creator circle. See our <Link to="/privacy" className="font-semibold text-[#9E4429] underline underline-offset-2">privacy policy</Link>.</span>
      </label>
      {error && <p role="alert" className="text-sm text-[#A9503A]" data-testid="creator-apply-error">{error}</p>}
      <button type="submit" disabled={state === "sending"} data-testid="creator-apply-submit"
        className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#2C221E] text-sm font-semibold text-[#FBF5EE] transition-colors hover:bg-[#9E4429] disabled:opacity-60 sm:w-auto sm:px-10">
        {state === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}Apply to the creator circle
      </button>
    </form>
  );
};

export default function CreatorsPage() {
  usePageMeta("Creators | Lunelle", "Join the Lunelle creator circle: apply, get your personal invite code and share Lunelle with your audience on launch day, 4 November.");
  return (
    <div className="min-h-screen" data-testid="creators-page">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-5 py-6 sm:px-8 lg:px-12">
        <Link to="/" data-testid="creators-home-link"><Logo /></Link>
        <Link to="/press" className="flex items-center gap-2 text-sm text-[#5C4A42] hover:text-[#2C221E]"><ArrowLeft className="h-4 w-4" /> Press kit</Link>
      </header>
      <main className="mx-auto max-w-6xl px-5 pb-28 pt-10 sm:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#9E4429]">The creator circle</p>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl font-light leading-[1.02] text-[#2C221E] sm:text-7xl">Share Lunelle <em className="italic text-[#9E4429]">with your people.</em></h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#3D302B]">For creators in wellness, faith, cycle syncing, food, style and money. Get a personal invite code and launch with us on November 4.</p>
        <div className="mt-8"><Countdown prefix="creators" /></div>
        <div className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-[#F4E6DC] px-5 py-4 text-[#2C221E]" data-testid="creators-perk">
          <Sparkles className="h-5 w-5 shrink-0 text-[#9E4429]" /><span><strong>Your perk:</strong> {PERK}</span>
        </div>

        <ol className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="creators-steps">
          {STEPS.map(([Icon, t, d], i) => (
            <li key={t} className="rounded-[1.5rem] border border-[#C86D51]/15 bg-white/70 p-6">
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#9E4429]"><Icon className="h-4 w-4" />Step {i + 1}</span>
              <p className="mt-3 font-serif text-2xl text-[#2C221E]">{t}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#5C4A42]">{d}</p>
            </li>
          ))}
        </ol>

        <section className="mt-16" id="apply">
          <h2 className="mb-6 font-serif text-4xl text-[#2C221E]">Apply</h2>
          <ApplyForm />
        </section>
      </main>
    </div>
  );
}
